// A plain N-slot item container with a transfer cooldown (M15.2). PURE (ENG-1).
//
// The shared shape behind hoppers (5 slots), droppers and dispensers (9 slots):
// an array of stacks plus the game-tick `cooldown` the logistics engine uses to
// pace transfers (RED-2: a hopper moves one item, then waits 8 gt). Container
// logic (pull/push/dispense) lives in game/logistics.ts; this file is just the
// storage + its `Slots` view, mirroring containers/chest.ts.
import { type ItemStack, type Slots, emptyStack, isEmpty, cloneStack } from '../inventory';

/** An N-slot container's persisted state (stacks + transfer cooldown). */
export interface ArrayContainer {
  items: (ItemStack | null)[];
  /** Game ticks until the next transfer is allowed (0 = ready). */
  cooldown: number;
}

export function newArrayContainer(size: number): ArrayContainer {
  return { items: Array.from({ length: size }, () => null), cooldown: 0 };
}

/** Non-empty stacks a broken container scatters. */
export function arrayContents(c: ArrayContainer): ItemStack[] {
  return c.items.filter((s): s is ItemStack => s != null && !isEmpty(s));
}

/** Index of the first non-empty slot, or -1. */
export function firstFilled(c: ArrayContainer): number {
  return c.items.findIndex((s) => s != null && !isEmpty(s));
}

/** A `Slots` view over the container for the window + transactions (PLR-1). */
export function arraySlots(c: ArrayContainer, maxStackOf: (item: number) => number): Slots {
  return {
    size: c.items.length,
    get: (i) => c.items[i] ?? emptyStack(),
    set: (i, s) => { c.items[i] = isEmpty(s) ? null : s; },
    maxStackOf: (item) => maxStackOf(item),
    accepts: () => true,
  };
}

/** Deep-clone container data (persistence snapshot). */
export function cloneArrayContainer(c: ArrayContainer): ArrayContainer {
  return {
    items: c.items.map((s) => (s && !isEmpty(s) ? cloneStack(s) : null)),
    cooldown: c.cooldown,
  };
}
